import type { Entity, EntityOfType, Property, Type } from '@cognitoforms/model.js';
import { isEntityType } from '@cognitoforms/model.js';
import type { Document } from '@cognitoforms/types/server-types/model/document';
import type { FileDataRef } from '@cognitoforms/types/server-types/model/file-data-ref';
import { hasDocumentContent, isDocumentFrozen } from '@cognitoforms/utils/document-file';
import { findPropertyPaths, isFieldShown, isFileDataRefProperty, visitEntity } from './model';

const DOCUMENT_TYPE_NAME = 'Document';
const SIGNATURE_TYPE_NAME = 'Signature';

export interface EntryDocumentAttachment {
	entity: Entity;
	property: Property;
	document: EntityOfType<Document>;
	shown: boolean;
}

export interface EntryUploadAttachment {
	entity: Entity;
	property: Property;
	file: FileDataRef;
}

function isContainedBy(property: Property, typeName: string): boolean {
	return !!property.containingType && property.containingType.fullName === typeName;
}

export function isDocumentProperty(property: Property): boolean {
	return isEntityType(property.propertyType) && property.propertyType.meta.fullName === DOCUMENT_TYPE_NAME;
}

export function findDocumentPropertyPaths(type: Type): Property[][] {
	return findPropertyPaths(type, isDocumentProperty, property => property.isList);
}

function hasContent(file: unknown): boolean {
	if (!file)
		return false;

	const fileEntity = file as Entity & { Size: number };
	const sizeProperty = fileEntity.meta.type.getProperty('Size');

	return !(sizeProperty && sizeProperty.isInited(fileEntity) && fileEntity.Size === 0);
}

function isUploadProperty(property: Property): boolean {
	return isFileDataRefProperty(property) && !isContainedBy(property, DOCUMENT_TYPE_NAME) && !isContainedBy(property, SIGNATURE_TYPE_NAME);
}

function isUploadFile(file: unknown): file is FileDataRef {
	return !!(file as FileDataRef | null | undefined)?.Id && hasContent(file);
}

function getUploadFiles(entity: Entity, property: Property): FileDataRef[] {
	const value = property.value(entity);
	const files = property.isList ? ((value as unknown[]) || []) : [value];

	return files.filter(isUploadFile);
}

export function getEntryUploadAttachments(entry: Entity): EntryUploadAttachment[] {
	const attachments: EntryUploadAttachment[] = [];

	if (!entry)
		return attachments;

	visitEntity(entry, (entity, property) => {
		if (!isUploadProperty(property))
			return;

		for (const file of getUploadFiles(entity, property))
			attachments.push({ entity, property, file });
	}, { followCircularProperties: false, followLookups: false });

	return attachments;
}

function entryHasUploads(entry: Entity): boolean {
	let hasUploads = false;

	visitEntity(entry, (entity, property) => {
		if (hasUploads || !isUploadProperty(property))
			return;

		if (getUploadFiles(entity, property).length > 0)
			hasUploads = true;
	}, { followCircularProperties: false, followLookups: false });

	return hasUploads;
}

function resolveDocumentOwner(entry: Entity, sections: Property[]): { entity: Entity, parentHidden: boolean } | null {
	let entity = entry;
	let parentHidden = false;

	for (const section of sections) {
		parentHidden = !isFieldShown(entity, section, { isRoot: entity === entry, parentHidden });
		entity = section.value(entity) as Entity;

		if (!entity)
			return null;
	}

	return { entity, parentHidden };
}

export function getEntryDocumentAttachments(entry: Entity): EntryDocumentAttachment[] {
	const attachments: EntryDocumentAttachment[] = [];

	if (!entry)
		return attachments;

	for (const path of findDocumentPropertyPaths(entry.meta.type)) {
		const owner = resolveDocumentOwner(entry, path.slice(0, -1));
		if (!owner)
			continue;

		const { entity, parentHidden } = owner;
		const property = path[path.length - 1];
		const document = property.value(entity) as EntityOfType<Document>;

		if (!hasDocumentContent(document))
			continue;

		const shown = isFieldShown(entity, property, { isRoot: entity === entry, parentHidden });

		if (shown || isDocumentFrozen(document))
			attachments.push({ entity, property, document, shown });
	}

	return attachments;
}

export function entryHasFiles(entry: Entity): boolean {
	if (!entry)
		return false;

	return entryHasUploads(entry) || getEntryDocumentAttachments(entry).length > 0;
}

export function entryHasDocumentFields(entry: Entity): boolean {
	return !!entry && findDocumentPropertyPaths(entry.meta.type).length > 0;
}
